/**
 * React Hook for Theme Management (CAND-14: Dark / Light Theme Toggle)
 */

import { useState, useEffect, useCallback } from 'react';
import type { ThemePreference, ResolvedTheme } from '../types';
import {
  getStoredThemePreference,
  setStoredThemePreference,
  applyTheme,
  resolveTheme,
  subscribeToSystemTheme,
} from '../services/themeService';

export function useTheme() {
  const [theme, setThemeState] = useState<ThemePreference>(() => getStoredThemePreference());
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => resolveTheme(theme));

  // Sync with document element and localStorage
  const setTheme = useCallback((newTheme: ThemePreference) => {
    setThemeState(newTheme);
    setStoredThemePreference(newTheme);
    const resolved = applyTheme(newTheme);
    setResolvedTheme(resolved);
  }, []);

  // Quick toggle between light and dark
  const toggleTheme = useCallback(() => {
    const nextTheme: ThemePreference = resolvedTheme === 'dark' ? 'light' : 'dark';
    setTheme(nextTheme);
  }, [resolvedTheme, setTheme]);

  // Apply on mount and listen to system OS changes
  useEffect(() => {
    const active = applyTheme(theme);
    setResolvedTheme(active);

    if (theme === 'system') {
      const unsubscribe = subscribeToSystemTheme((sysTheme) => {
        applyTheme('system');
        setResolvedTheme(sysTheme);
      });
      return unsubscribe;
    }
  }, [theme]);

  return {
    theme,
    resolvedTheme,
    setTheme,
    toggleTheme,
    isDark: resolvedTheme === 'dark',
  };
}
